import { ReactNode, useContext, useMemo } from 'react';
import { AppScope, appScopeUtils } from '../appScope.types';
import { AppScopeProvider } from './AppScopeProvider';
import { AppScopeContext } from './AppScopeContext';
import { isInitializedAppScope } from './internalAppScope';

/**
 * Scope for UI that v1 renders away from its opener (ModalHandler, the
 * drawers): the scope the opener stashed in Redux wins; without one, the
 * organization of the ambient context — never a nested browsing scope
 * inferred from the URL.
 */
export function StashedAppScope({
  stashed,
  children
}: {
  stashed: AppScope | null;
  children: ReactNode;
}) {
  const contextScope = useContext(AppScopeContext);

  const scope = useMemo((): AppScope => {
    if (stashed) {
      appScopeUtils.assertValid(stashed);
      return stashed;
    }
    if (contextScope && isInitializedAppScope(contextScope)) {
      return {
        level: 'organization',
        organizationId: contextScope.organizationId
      };
    }
    throw new Error(
      'AppScope is not initialized yet — portaled UI mounted before organizationId was available'
    );
  }, [stashed, contextScope]);

  return <AppScopeProvider scope={scope}>{children}</AppScopeProvider>;
}
